import { useState, useEffect, useCallback, useRef } from 'react';
import { SessionType, TimerState, TimerSettings } from '../types';
import { minutesToSeconds } from '../utils/timeFormatters';
import { playSessionCompleteSound } from '../utils/audioNotifications';

// Helper function moved outside to avoid recreation on every render
const getDurationForSession = (sessionType: SessionType, settings: TimerSettings): number => {
  switch (sessionType) {
    case SessionType.WORK:
      return settings.workDuration;
    case SessionType.SHORT_BREAK:
      return settings.shortBreakDuration;
    case SessionType.LONG_BREAK:
      return settings.longBreakDuration;
  }
};

export function usePomodoro(settings: TimerSettings, onPomodoroCompleted?: () => void) {
  // Use ref to store callback to avoid it being in dependency array
  const onPomodoroCompletedRef = useRef(onPomodoroCompleted);
  const lastCompletionTimeRef = useRef<number>(0);
  
  // Update ref when callback changes
  useEffect(() => {
    onPomodoroCompletedRef.current = onPomodoroCompleted;
  }, [onPomodoroCompleted]);

  const [timerState, setTimerState] = useState<TimerState>({
    timeLeft: minutesToSeconds(settings.workDuration),
    sessionType: SessionType.WORK,
    isRunning: false,
    isCompleted: false,
    pomodoroCount: 1,
    totalPomodoros: 0
  });

  // Update timer when settings change (only if not running) - Fixed dependency array
  useEffect(() => {
    if (!timerState.isRunning) {
      const newDuration = getDurationForSession(timerState.sessionType, settings);
      setTimerState(prev => ({
        ...prev,
        timeLeft: minutesToSeconds(newDuration)
      }));
    }
  }, [settings.workDuration, settings.shortBreakDuration, settings.longBreakDuration, timerState.sessionType, timerState.isRunning]);
  // Timer countdown effect - Removed onPomodoroCompleted from dependencies
  useEffect(() => {
    let interval: number | null = null;

    if (timerState.isRunning && timerState.timeLeft > 0) {
      interval = setInterval(() => {
        setTimerState(prev => {
          const newTimeLeft = prev.timeLeft - 1;
          
          // Check if session is complete
          if (newTimeLeft <= 0) {
            if (settings.soundEnabled) {
              playSessionCompleteSound();
            }

            // If a work session just completed, track it in stats with debounce
            if (prev.sessionType === SessionType.WORK && onPomodoroCompletedRef.current) {
              const now = Date.now();
              // Prevent double execution within 2 seconds
              if (now - lastCompletionTimeRef.current > 2000) {
                console.log('🍅 Pomodoro completed, calling callback');
                lastCompletionTimeRef.current = now;
                onPomodoroCompletedRef.current();
              } else {
                console.log('🚫 Prevented duplicate Pomodoro completion callback');
              }
            }

            return {
              ...prev,
              timeLeft: 0,
              isRunning: false,
              isCompleted: true
            };
          }
          
          return {
            ...prev,
            timeLeft: newTimeLeft
          };
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerState.isRunning, timerState.timeLeft, settings.soundEnabled]);

  const startTimer = useCallback(() => {
    setTimerState(prev => ({
      ...prev,
      isRunning: true,
      isCompleted: false
    }));
  }, []);

  const pauseTimer = useCallback(() => {
    setTimerState(prev => ({
      ...prev,
      isRunning: false
    }));
  }, []);

  const resetTimer = useCallback(() => {
    const duration = getDurationForSession(timerState.sessionType, settings);

    setTimerState(prev => ({
      ...prev,
      timeLeft: minutesToSeconds(duration),
      isRunning: false,
      isCompleted: false
    }));
  }, [timerState.sessionType, settings]);

  const nextSession = useCallback(() => {
    let nextSessionType: SessionType;
    let newPomodoroCount = timerState.pomodoroCount;
    let newTotalPomodoros = timerState.totalPomodoros;

    if (timerState.sessionType === SessionType.WORK) {
      // Completed a work session
      newTotalPomodoros += 1;
      
      if (timerState.pomodoroCount >= 4) {
        // Time for long break
        nextSessionType = SessionType.LONG_BREAK;
        newPomodoroCount = 1; // Reset cycle
      } else {
        // Time for short break
        nextSessionType = SessionType.SHORT_BREAK;
        newPomodoroCount += 1;
      }
    } else {
      // Completed a break session
      nextSessionType = SessionType.WORK;
    }

    const duration = getDurationForSession(nextSessionType, settings);

    setTimerState({
      timeLeft: minutesToSeconds(duration),
      sessionType: nextSessionType,
      isRunning: false,
      isCompleted: false,
      pomodoroCount: newPomodoroCount,
      totalPomodoros: newTotalPomodoros
    });
  }, [timerState.sessionType, timerState.pomodoroCount, timerState.totalPomodoros, settings]);

  const toggleTimer = useCallback(() => {
    if (timerState.isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  }, [timerState.isRunning, startTimer, pauseTimer]);

  const skipSession = useCallback(() => {
    nextSession();
  }, [nextSession]);

  // Auto-transition effect when session completes and auto-start is enabled
  useEffect(() => {
    if (timerState.isCompleted && settings.autoStart) {
      const timeoutId = setTimeout(() => {
        nextSession();
        // Auto-start the next session if auto-start is enabled
        setTimeout(() => {
          startTimer();
        }, 100);
      }, 2000); // Wait 2 seconds before auto-transitioning

      return () => clearTimeout(timeoutId);
    }
  }, [timerState.isCompleted, settings.autoStart, nextSession, startTimer]);

  return {
    timerState,
    startTimer,
    pauseTimer,
    resetTimer,
    nextSession,
    skipSession,
    toggleTimer
  };
} 